import React, { useContext, memo } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { Prompt } from 'react-router-dom';
import { Grid, FormControl } from '@mui/material';
import { Autocomplete, TextField, Button, Typography, Slider } from '@totalsoft/rocket-ui';
import DetailedSlider from 'components/common/inputs/DetailedSlider';
import { RightsContext } from 'providers/RightsContext';
import { ParamsContext } from 'providers/ParamsContext';
import { GridActionsWrapper, SliderWrapper } from 'components/common/CommonStyles';
import DetailedTextField from 'components/common/inputs/DetailedTextField';
import WrappedLinearProgress from 'components/common/WrappedLinearProgress';

const OfferFinancingStructureComponentAlpha = ({
  document,
  isDirty,
  validationInfo,
  t,
  maxResidualValuePercentLoading,
  saving,
  onSave,
  onOpenNegotiationModal,
  onPropertyChange,
  offerFinancialProductRuleReadOnlyFields,
  areGuaranteeRatesVisible,
  priceTypes,
  interestTypeName
}) => {
  const { financingStructureWrite, offerAquisitionPriceNegotiation, readOnlyFull, isImage } = useContext(RightsContext);
  const { cvsChPercent, cvsChAmount } = useContext(ParamsContext);

  return (
    <form noValidate>
      <Prompt when={isDirty} message={t('LeavingWithoutSaving')} />
      <Grid container spacing={4} px={1}>
        <Grid item xs={12} sm={6} md={6} lg={6}>
          <TextField
            id={'offerFinancingStructureCard_financialProductName'}
            fullWidth
            value={document.financialProductName || ''}
            label={t('OfferEdit.FinancingStructure.FinancialProductName')}
            disabled
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <TextField
            id={'offerFinancingStructureCard_contractTypeName'}
            fullWidth
            value={document.contractTypeName || ''}
            label={t('OfferEdit.FinancingStructure.ContractTypeName')}
            disabled
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <TextField
            id={'offerFinancingStructureCard_currency'}
            fullWidth
            value={document.currencyCode || ''}
            label={t('OfferEdit.FinancingStructure.Currency')}
            disabled
          />
        </Grid>
        {!document.hideCreditTVA && (
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Autocomplete
              id={'offerFinancingStructureCard_priceType'}
              fullWidth
              value={document.priceTypeId || ''}
              valueKey='dictionaryItemId'
              labelKey='dictionaryItemName'
              label={t('OfferEdit.FinancingStructure.PriceType')}
              options={priceTypes}
              onChange={onPropertyChange('priceTypeId')}
              isClearable={false}
              simpleValue={true}
              disabled={readOnlyFull}
            />
          </Grid>
        )}
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <TextField
            id={'offerFinancingStructureCard_totalAcquisitionPrice'}
            fullWidth
            disabled
            isNumeric
            value={document.totalAcquisitionPrice || ''}
            inputProps={{ decimalScale: cvsChAmount }}
            label={t('OfferEdit.FinancingStructure.TotalAcquisitionPrice')}
          />
        </Grid>
        {!document.hideInfoForDealersByContractTypes && (
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <DetailedTextField
              id={'offerFinancingStructureCard_interestPercent'}
              value={document.interestPercent}
              isNumeric
              fullWidth
              defaultValue={document.defaultInterestPercent}
              auxiliaryInfo={t('OfferEdit.FinancingStructure.InitialValue', {
                data: document.defaultInterestPercent
              })}
              helperText={validationInfo.interestPercent}
              error={Boolean(validationInfo.interestPercent)}
              onChange={onPropertyChange('interestPercent')}
              decimalScale={cvsChPercent}
              label={`${t('OfferEdit.FinancingStructure.InterestPercent')} (${interestTypeName})`}
              disabled={readOnlyFull || !financingStructureWrite || offerFinancialProductRuleReadOnlyFields.interestPercentReadOnly}
            />
          </Grid>
        )}
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <DetailedSlider
            id={'offerFinancingStructureCard_advancePercent'}
            value={document.advancePercent || 0}
            defaultValue={document.defaultAdvancePercent}
            min={document.minAdvancePercent}
            max={document.maxAdvancePercent}
            step={1}
            fullWidth
            label={t('OfferEdit.FinancingStructure.AdvancePercent')}
            onChange={onPropertyChange('advancePercent')}
            decimalScale={cvsChPercent}
            auxiliaryInfo={t('OfferEdit.FinancingStructure.InitialValue', {
              data: document.defaultAdvancePercent
            })}
            error={Boolean(validationInfo.advancePercent)}
            helperText={validationInfo.advancePercent}
            disabled={readOnlyFull || !financingStructureWrite || offerFinancialProductRuleReadOnlyFields.advancePercentReadOnly}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <DetailedTextField
            id={'offerFinancingStructureCard_advance'}
            value={document.advance == null ? '' : document.advance}
            decimalScale={cvsChAmount}
            isNumeric
            fullWidth
            defaultValue={document.defaultAdvance}
            auxiliaryInfo={t('OfferEdit.FinancingStructure.InitialValue', {
              data: document.defaultAdvance
            })}
            error={Boolean(validationInfo.advance)}
            helperText={validationInfo.advance}
            onChange={onPropertyChange('advance')}
            label={t('OfferEdit.FinancingStructure.Advance')}
            disabled={readOnlyFull || !financingStructureWrite}
          />
        </Grid>
        {!document.hideInfoForDealersByContractTypes && (
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <SliderWrapper>
              <Slider
                fullWidth
                id={'offerFinancingStructureCard_financingValuePercent'}
                value={document.financingValuePercent || 0}
                min={100 - document.maxAdvancePercent}
                max={100 - document.minAdvancePercent}
                step={1}
                label={t('OfferEdit.FinancingStructure.FinancingValuePercent')}
                decimalScale={cvsChPercent}
                error={Boolean(validationInfo.financingValuePercent)}
                helperText={validationInfo.financingValuePercent}
                disabled
              />
            </SliderWrapper>
          </Grid>
        )}
        {!document.hideInfoForDealersByContractTypes && (
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <TextField
              id={'offerFinancingStructureCard_financingValue'}
              inputProps={{ decimalScale: cvsChAmount }}
              fullWidth
              disabled
              isNumeric
              value={document.financingValue || 0}
              label={t('OfferEdit.FinancingStructure.FinancingValue')}
            />
          </Grid>
        )}
        {!document.hideInfoForDealersByContractTypes && (
          <>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              {maxResidualValuePercentLoading ? (
                <FormControl fullWidth>
                  <Typography sx={{ mb: 2 }}>{t('OfferEdit.FinancingStructure.ResidualValuePercent')}</Typography>
                  <WrappedLinearProgress />
                </FormControl>
              ) : (
                <DetailedSlider
                  id={'offerFinancingStructureCard_residualValuePercent'}
                  value={document.residualValuePercent || 0}
                  defaultValue={document.defaultResidualValuePercent}
                  min={document.minResidualValuePercent}
                  max={document.maxResidualValuePercent}
                  step={1}
                  fullWidth
                  decimalScale={cvsChPercent}
                  label={t('OfferEdit.FinancingStructure.ResidualValuePercent')}
                  onChange={onPropertyChange('residualValuePercent')}
                  auxiliaryInfo={t('OfferEdit.FinancingStructure.InitialValue', {
                    data: document.defaultResidualValuePercent
                  })}
                  error={Boolean(validationInfo.residualValuePercent)}
                  helperText={validationInfo.residualValuePercent}
                  disabled={readOnlyFull || !financingStructureWrite || offerFinancialProductRuleReadOnlyFields.rezidualPercentReadOnly}
                />
              )}
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={3}>
              {maxResidualValuePercentLoading ? (
                <FormControl fullWidth>
                  <Typography sx={{ mb: 2 }}>{t('OfferEdit.FinancingStructure.ResidualValue')}</Typography>
                  <WrappedLinearProgress />
                </FormControl>
              ) : (
                <DetailedTextField
                  id={'offerFinancingStructureCard_residualValue'}
                  value={document.residualValue == null ? '' : document.residualValue}
                  decimalScale={cvsChAmount}
                  fullWidth
                  isNumeric
                  defaultValue={document.defaultResidualValue}
                  auxiliaryInfo={t('OfferEdit.FinancingStructure.InitialValue', {
                    data: document.defaultResidualValue
                  })}
                  error={Boolean(validationInfo.residualValue)}
                  helperText={validationInfo.residualValue}
                  onChange={onPropertyChange('residualValue')}
                  label={t('OfferEdit.FinancingStructure.ResidualValue')}
                  disabled={readOnlyFull || !financingStructureWrite || offerFinancialProductRuleReadOnlyFields.rezidualAmountReadOnly}
                />
              )}
            </Grid>
          </>
        )}
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <DetailedSlider
            id={'offerFinancingStructureCard_managementFeePercent'}
            value={document.managementFeePercent || 0}
            defaultValue={document.defaultManagementFeePercent}
            min={document.minManagementFeePercent}
            max={document.maxManagementFeePercent}
            fullWidth
            step={1}
            decimalScale={cvsChPercent}
            label={t('OfferEdit.FinancingStructure.ManagementFeePercent')}
            onChange={onPropertyChange('managementFeePercent')}
            auxiliaryInfo={t('OfferEdit.FinancingStructure.InitialValue', {
              data: document.defaultManagementFeePercent
            })}
            error={Boolean(validationInfo.managementFeePercent)}
            helperText={validationInfo.managementFeePercent}
            disabled={readOnlyFull || !financingStructureWrite || offerFinancialProductRuleReadOnlyFields.managementFeePercentReadOnly}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <TextField
            fullWidth
            isNumeric
            id={'offerFinancingStructureCard_managementFee'}
            value={document.managementFee == null ? '' : document.managementFee}
            inputProps={{ decimalScale: cvsChAmount }}
            error={validationInfo && validationInfo.managementFee ? true : false}
            helperText={validationInfo?.managementFee}
            onChange={onPropertyChange('managementFee')}
            label={t('OfferEdit.FinancingStructure.ManagementFee')}
            disabled={readOnlyFull || !financingStructureWrite || offerFinancialProductRuleReadOnlyFields.managementFeeReadOnly}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <DetailedSlider
            id={'offerFinancingStructureCard_durationMonths'}
            value={document.durationMonths || 0}
            defaultValue={document.defaultDuration}
            min={document.minDurationMonths}
            max={document.maxDurationMonths}
            step={1}
            label={t('OfferEdit.FinancingStructure.DurationMonths')}
            onChange={onPropertyChange('durationMonths')}
            fullWidth
            decimalScale={0}
            auxiliaryInfo={t('OfferEdit.FinancingStructure.InitialValue', {
              data: document.defaultDuration
            })}
            error={Boolean(validationInfo.durationMonths)}
            helperText={validationInfo.durationMonths}
            disabled={readOnlyFull || !financingStructureWrite || offerFinancialProductRuleReadOnlyFields.durationReadOnly}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <TextField
            id={'offerFinancingStructureCard_paymentPlanName'}
            fullWidth
            value={document.paymentPlanName || ''}
            label={t('OfferEdit.FinancingStructure.ScheduleType')}
            disabled={readOnlyFull}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <TextField
            id={'offerFinancingStructureCard_monthlyPayment'}
            fullWidth
            disabled
            isNumeric
            value={document.monthlyPayment || ''}
            inputProps={{ decimalScale: cvsChAmount }}
            label={t('OfferEdit.FinancingStructure.MonthlyPayment')}
          />
        </Grid>
        {areGuaranteeRatesVisible && (
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <TextField
              id={'offerFinancingStructureCard_numberOfGuaranteeRates'}
              disabled={readOnlyFull || document.numberOfGuaranteeRatesIsReadOnly || !financingStructureWrite}
              fullWidth
              isNumeric
              value={document.numberOfGuaranteeRates || ''}
              inputProps={{ decimalScale: 0 }}
              label={t('OfferEdit.FinancingStructure.NumberOfGuaranteeRates')}
              onChange={onPropertyChange('numberOfGuaranteeRates')}
              error={validationInfo.numberOfGuaranteeRates ? true : false}
              helperText={validationInfo.numberOfGuaranteeRates}
            />
          </Grid>
        )}
        {document.logisticTaxExists && (
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <TextField
              id={'offerFinancingStructureCard_logisticTax'}
              disabled={readOnlyFull || document.logisticTax.isReadOnly}
              fullWidth
              isNumeric
              inputProps={{ decimalScale: cvsChAmount }}
              value={document.logisticTax.value || ''}
              onChange={onPropertyChange('logisticTax.value')}
              label={t('OfferEdit.FinancingStructure.LogisticTax')}
              helperText={validationInfo.logisticTax && validationInfo.logisticTax.value}
              error={validationInfo.logisticTax && !validationInfo.logisticTax.isValid}
            />
          </Grid>
        )}
        {document.vatFinancing && (
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Autocomplete
              id={'offerAdditionalDataCard_vatFinancing'}
              fullWidth
              value={document.vatFinancing.value}
              valueKey='value'
              labelKey='value'
              label={document.vatFinancing.name}
              options={document.vatFinancing.vatFinancingOptions}
              onChange={onPropertyChange('vatFinancing.value')}
              isClearable={false}
              simpleValue={true}
              disabled={readOnlyFull}
            />
          </Grid>
        )}
        {!isImage && (
          <GridActionsWrapper item xs={12}>
            <Button
              id={'offerFinancingStructureCard_saveBtn'}
              onClick={onSave}
              disabled={readOnlyFull || saving || !validationInfo.isValid || !financingStructureWrite || !isDirty}
            >
              {saving ? t('Saving') + '...' : t('Save')}
            </Button>
            {offerAquisitionPriceNegotiation && (
              <Button
                id={'offerFinancingStructureCard_negotiateBtn'}
                onClick={() => onOpenNegotiationModal()}
                disabled={readOnlyFull || saving || !validationInfo.isValid}
              >
                {t('OfferEdit.FinancingStructure.Negotiate')}
              </Button>
            )}
          </GridActionsWrapper>
        )}
      </Grid>
    </form>
  );
};

OfferFinancingStructureComponentAlpha.displayName = 'OfferFinancingStructureComponent';
OfferFinancingStructureComponentAlpha.propTypes = {
  document: PropTypes.object.isRequired,
  isDirty: PropTypes.bool.isRequired,
  validationInfo: PropTypes.object.isRequired,
  onSave: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired,
  onOpenNegotiationModal: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  maxResidualValuePercentLoading: PropTypes.bool.isRequired,
  offerFinancialProductRuleReadOnlyFields: PropTypes.object.isRequired,
  areGuaranteeRatesVisible: PropTypes.bool.isRequired,
  priceTypes: PropTypes.array,
  interestTypeName: PropTypes.string
};

export default withTranslation()(memo(OfferFinancingStructureComponentAlpha));
